import { memo, type FC } from "react";
import Box from "@mui/material/Box";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import InputAdornment from "@mui/material/InputAdornment";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import SearchIcon from "@mui/icons-material/Search";
import { filterStatesBySearch } from "../utils/geoSelection";

const denseItemSx = {
  display: "flex",
  alignItems: "center",
  ml: 0,
  my: 0,
  "& .MuiCheckbox-root": { py: 0.25 },
  "& .MuiFormControlLabel-label": { my: 0 },
} as const;

type StateOption = { id: string; name: string };

type GeoStatesCheckboxListProps = {
  title?: string;
  action?: React.ReactNode;
  states: StateOption[];
  selectedStateIds: string[];
  onToggle: (stateId: string) => void;
  searchValue: string;
  onSearchChange: (value: string) => void;
  loading?: boolean;
  disabled?: boolean;
  emptyMessage?: string;
  noResultsMessage?: string;
  maxHeight?: number;
  searchInputId?: string;
};

export const GeoStatesCheckboxList: FC<GeoStatesCheckboxListProps> = memo(
  ({
    title,
    action,
    states,
    selectedStateIds,
    onToggle,
    searchValue,
    onSearchChange,
    loading,
    disabled,
    emptyMessage = "No states available.",
    noResultsMessage = "No states found.",
    maxHeight = 250,
    searchInputId,
  }) => {
    const filteredStates = filterStatesBySearch(states, searchValue);

    return (
      <Box>
        {(title || action) && (
          <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            justifyContent="space-between"
            sx={{ mb: 0.5 }}
          >
            {title ? (
              <Typography variant="subtitle2" fontWeight={700}>
                {title}
              </Typography>
            ) : (
              <Box sx={{ flex: 1 }} />
            )}
            {action}
          </Stack>
        )}
        <TextField
          id={searchInputId}
          placeholder="Search states..."
          size="small"
          fullWidth
          value={searchValue}
          onChange={(e) => onSearchChange(e.target.value)}
          disabled={disabled || loading}
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon
                    fontSize="small"
                    sx={{ color: "text.secondary" }}
                  />
                </InputAdornment>
              ),
            },
          }}
          sx={{ mb: 1 }}
        />

        {loading ? (
          <Typography variant="body2" color="text.secondary">
            Loading states...
          </Typography>
        ) : states.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            {emptyMessage}
          </Typography>
        ) : filteredStates.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            {noResultsMessage}
          </Typography>
        ) : (
          <Box sx={{ maxHeight, overflow: "auto" }}>
            {filteredStates.map((state) => {
              const isSelected = selectedStateIds.includes(state.id);
              return (
                <FormControlLabel
                  key={state.id}
                  control={
                    <Checkbox
                      size="small"
                      checked={isSelected}
                      onChange={() => onToggle(state.id)}
                      sx={{ pl: 0 }}
                    />
                  }
                  label={<Typography variant="body2">{state.name}</Typography>}
                  sx={denseItemSx}
                />
              );
            })}
          </Box>
        )}
      </Box>
    );
  }
);

GeoStatesCheckboxList.displayName = "GeoStatesCheckboxList";
